import { Button, Group, Paper, Skeleton, Table, Text, Tooltip } from '@mantine/core';
import type { DatesRangeValue } from '@mantine/dates';
import type { AuditEvent } from '@motionsafe/ms-console-api/admin/v1/audit_pb.js';
import type { Device } from '@motionsafe/ms-console-api/admin/v1/device_pb.js';
import { IconDownload, IconShare } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { api } from '../../api/client';
import { devicesByIdQuery, errorText, orgQuery, useOrgNames } from '../../api/hooks';
import { AuditRange, EventDetail, EventTerms, HAIRLINE, None, PartyCell, WRAP, useAuditPages } from '../../components/audit-events';
import { useGridRows } from '../../components/grid';
import { DrawerSection, EmptyState, PageHeader, ToolbarActions } from '../../components/page';
import { PageFooter } from '../../components/pager';
import { PaneDrawer } from '../../components/pane';
import { when } from '../../lib/format';
import { hardwareEnding } from '../../lib/hardware';
import { COUNTERPART_ACTIONS, detailNumber, detailText, eventWord, halvesByShare, isDecrypted, linkCaption, peerFor, peerText, recordedLater, shareIdsOf, SHARE_ENDED, SHARING_ACTIONS, transportName, type Peer } from '../../lib/sharing';
import { ICON_INLINE } from '../../theme';
import { useOrgId, useTopLevel } from '../_app';

// Every collection shared from a device, encrypted or decrypted, every one a
// device received, and every transfer refused as another organization's
// (ARCHITECTURE.md §14). Devices report them through their outbox, so one
// shared offline arrives at the next sync and says how much later. Each
// row's other half, end, and refusal are looked up by share ID, whatever
// page they are on. Org admins read their own organization; staff read any,
// and every organization's on their own page (/all-sharing).
export const Route = createFileRoute('/_app/sharing')({ component: Sharing });

const COLUMNS = ['When', 'Event', 'Operator', 'Device', 'VIN', 'Transport', 'From / To', 'Decrypted', 'Justification', 'Ended'] as const;
// Operator and justification share what is left.
const COLUMN_WIDTH: Record<string, number> = { When: 190, Organization: 150, Event: 170, Device: 150, VIN: 200, Transport: 120, 'From / To': 220, Decrypted: 100, Ended: 190 };

// A device as the Devices page names it: the organization's label, then the
// end of its device identifier. One the list does not have is its record ID.
function DeviceCell({ id, device }: { id: string; device: Device | undefined }) {
  if (!id) return <None />;
  if (!device) return <span className="id" style={WRAP}>{id}</span>;
  return (
    <>
      {device.label && <Text size="sm" fw={600} style={WRAP}>{device.label}</Text>}
      {device.hardwareId
        ? <Text size="sm" className="id" aria-label={`Device identifier ending ${hardwareEnding(device.hardwareId)}`}>{hardwareEnding(device.hardwareId)}</Text>
        : !device.label && <span className="id" style={WRAP}>{id}</span>}
    </>
  );
}

// Who shared: the operator signed in on the device. One the device named but
// who had not signed in on it is shown by the ID it gave, marked so.
function OperatorCell({ e }: { e: AuditEvent }) {
  if (e.actor?.type === 'user') return <PartyCell party={e.actor} />;
  const unverified = detailText(e, 'operator_sub_unverified');
  return unverified
    ? <><span className="id" style={WRAP}>{unverified}</span><Text size="xs" c="dimmed">not signed in on this device</Text></>
    : <None />;
}

// Who was on the other end of a Wi-Fi Direct transfer: from the console's
// own record once the other half has synced, what the other device said
// about itself until then.
const PEER_CLAIMED = 'As reported by the other device';
const PEER_CONFIRMED = 'From the other device\'s own record';

function PeerCell({ peer }: { peer: Peer | null }) {
  if (!peer) return null;
  return (
    <Tooltip label={peer.confirmed ? PEER_CONFIRMED : PEER_CLAIMED} openDelay={300}>
      <Text size="sm" style={WRAP}>{peerText(peer)}</Text>
    </Tooltip>
  );
}

// How a share ended, by its end event: when, by the device's clock, and the reason as recorded.
function endLine(end: AuditEvent): string {
  const minutes = detailNumber(end, 'minutes');
  const ended = detailText(end, 'ended');
  return [detailText(end, 'reason') || 'ended', minutes !== null ? `after ${minutes} min` : '', ended ? ended.replace(/_/g, ' ') : ''].filter(Boolean).join(', ');
}

export function Sharing() {
  const top = useTopLevel();
  const orgId = useOrgId();
  const inView = useQuery({ ...orgQuery(orgId), enabled: !top }).data;
  const columns = top ? ['When', 'Organization', ...COLUMNS.slice(1)] : [...COLUMNS];
  const [range, setRange] = useState<DatesRangeValue<string>>([null, null]);
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const [paneOpen, setPaneOpen] = useState(false);
  const { events, rows, total, page, setPage, dated, exportCsv, exporting } = useAuditPages({ top, orgId, actions: SHARING_ACTIONS, range });
  // Every event of the shares on this page, in one lookup by share ID:
  // the other half, the end, and any refusal.
  const shareIds = shareIdsOf(rows);
  const halves = useQuery({
    queryKey: ['audit', 'share-halves', top ? 'all' : orgId, shareIds.join(',')],
    queryFn: () => api.audit.queryAudit({ orgId: top ? '' : orgId, allOrganizations: top, actions: COUNTERPART_ACTIONS, shareIds, pageSize: 500 }),
    enabled: shareIds.length > 0,
  });
  const byShare = halvesByShare(halves.data?.events ?? []);
  // The organizations and devices this page names, read for those alone: revoked devices included; for staff, any organization's.
  const named = useOrgNames([...rows, ...(halves.data?.events ?? [])].map((e) => e.orgId), top);
  const orgName = (id: string) => (id ? named(id) || 'unknown' : 'MotionSafe');
  // An organization by name for the other half of a transfer; '' leaves the device's claim.
  const peerOrgName = (id: string) => (top ? named(id) : id === orgId ? inView?.name ?? '' : '');
  const pageDevices = useQuery({ ...devicesByIdQuery(orgId, rows.map((e) => e.deviceId).filter(Boolean), top), placeholderData: keepPreviousData }).data;
  const devices = new Map((pageDevices ?? []).map((d) => [d.id, d]));
  const halvesOf = (e: AuditEvent) => byShare.get(detailText(e, 'share_id'));
  const showTable = events.isPending || rows.length > 0;
  const open = (e: AuditEvent) => { setSelected(e); setPaneOpen(true); };
  const gridRow = useGridRows(rows.map((e) => e.id));
  const peerOf = (e: AuditEvent) => peerFor(e, halvesOf(e), peerOrgName);
  // A send's end, or the refusal that stood in for one.
  const endOf = (e: AuditEvent) => (e.action === 'collection.shared' ? halvesOf(e)?.ended : undefined);
  const refusalOf = (e: AuditEvent) => (e.action === 'collection.shared' ? halvesOf(e)?.shareRefused ?? halvesOf(e)?.transferRefused : undefined);
  const selectedEnd = selected ? endOf(selected) : undefined;
  const selectedPeer = selected ? peerOf(selected) : null;

  return (
    <>
      <PageHeader
        title={top ? 'All shares' : 'Sharing audit'}
        description={top
          ? 'Every collection shared from a device in every organization, encrypted or decrypted, every one a device received, and every transfer refused because it was another organization\'s. Newest first.'
          : 'Every collection shared from this organization\'s devices, encrypted or decrypted, every one a device received, and every transfer refused because it was another organization\'s. Newest first. A device reports each at its next sync, so one shared offline arrives later and says so.'}
      />

      <Group gap="sm" align="center" wrap="wrap" mb="md">
        <AuditRange value={range} onChange={setRange} />
        {events.isSuccess && rows.length > 0 && (
          <Text size="sm" c="dimmed" className="num" ml="auto">
            {total === 1 ? '1 event' : `${total} events`}
          </Text>
        )}
        {/* The ends go with the shares, so the export can be joined as the table is. */}
        <ToolbarActions><Button size="sm" variant="default" leftSection={<IconDownload {...ICON_INLINE} />} onClick={() => void exportCsv([...SHARING_ACTIONS, SHARE_ENDED])} disabled={exporting}>Export CSV</Button></ToolbarActions>
      </Group>

      <Paper p={0} style={{ overflow: 'hidden' }}>
        {showTable && (
          <Table.ScrollContainer minWidth={top ? 1720 : 1570}>
            <Table role="grid" layout="fixed" aria-label="Shares, received collections, and refused transfers" horizontalSpacing="md" verticalSpacing="sm" style={{ opacity: events.isPlaceholderData ? 0.6 : 1, transition: 'opacity var(--motion-micro, 120ms) ease-out' }}>
              <Table.Thead>
                <Table.Tr>{columns.map((c) => <Table.Th key={c} {...(COLUMN_WIDTH[c] ? { w: COLUMN_WIDTH[c] } : {})}>{c}</Table.Th>)}</Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {events.isPending && [0, 1, 2, 3, 4, 5].map((i) => (
                  <Table.Tr key={`loading-${i}`} aria-hidden="true" bg="transparent">
                    {columns.map((c) => <Table.Td key={c}><Skeleton height={14} width="65%" my={5} /></Table.Td>)}
                  </Table.Tr>
                ))}
                {rows.map((e) => {
                  const isSelected = paneOpen && selected?.id === e.id;
                  const late = recordedLater(e);
                  const vin = detailText(e, 'vin');
                  const vehicleId = detailText(e, 'vehicle_id');
                  const transport = detailText(e, 'transport');
                  const justification = detailText(e, 'justification');
                  const end = endOf(e);
                  const refusal = refusalOf(e);
                  const linked = linkCaption(e, halvesOf(e));
                  return (
                    <Table.Tr
                      key={e.id}
                      {...gridRow(e.id, () => open(e))}
                      aria-selected={isSelected}
                      onClick={() => open(e)}
                      style={{ cursor: 'pointer', verticalAlign: 'top' }}
                      {...(isSelected ? { bg: 'var(--mantine-primary-color-light)' } : {})}
                    >
                      <Table.Td className="num">
                        <Text size="sm" style={{ whiteSpace: 'nowrap' }}>{when(e.occurredAt)}</Text>
                        {late && <Text size="xs" c="dimmed">{late}</Text>}
                      </Table.Td>
                      {top && <Table.Td>{e.orgId ? orgName(e.orgId) : <Text span inherit c="dimmed">MotionSafe</Text>}</Table.Td>}
                      <Table.Td>
                        <Text size="sm" style={WRAP}>{eventWord(e.action)}</Text>
                        {linked && <Text size="xs" c="dimmed">{linked}</Text>}
                      </Table.Td>
                      <Table.Td><OperatorCell e={e} /></Table.Td>
                      <Table.Td><DeviceCell id={e.deviceId} device={devices.get(e.deviceId)} /></Table.Td>
                      <Table.Td>
                        {vin
                          ? <span className="id" style={WRAP}>{vin}</span>
                          : vehicleId
                            ? <><span className="id" style={WRAP}>{vehicleId}</span><Text size="xs" c="dimmed">No VIN; the vehicle&apos;s ID on the device</Text></>
                            : <None />}
                      </Table.Td>
                      <Table.Td>{transport ? transportName(transport) : <None />}</Table.Td>
                      <Table.Td><PeerCell peer={peerOf(e)} /></Table.Td>
                      <Table.Td>{isDecrypted(e) ? <Text size="sm" fw={600}>Yes</Text> : null}</Table.Td>
                      <Table.Td>{justification ? <Text size="sm" lineClamp={2} style={WRAP}>{justification}</Text> : null}</Table.Td>
                      <Table.Td>
                        {end
                          ? <><Text size="sm" className="num" style={{ whiteSpace: 'nowrap' }}>{when(end.occurredAt)}</Text><Text size="xs" c="dimmed">{endLine(end)}</Text></>
                          : refusal
                            ? <><Text size="sm" className="num" style={{ whiteSpace: 'nowrap' }}>{when(refusal.occurredAt)}</Text><Text size="xs" c="dimmed">Refused by another organization</Text></>
                            : e.action === 'collection.shared' && halves.isSuccess ? <Text size="sm" c="dimmed">none recorded</Text> : null}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {events.isSuccess && rows.length === 0 && (
          dated
            ? <EmptyState icon={IconShare} title="Nothing in this range" body="No collection was shared, received, or refused on these dates. Widen the range or clear it." />
            : <EmptyState icon={IconShare} title="No shares recorded yet" body="Each collection an operator shares from a device, encrypted or decrypted, and each one a device receives, is recorded here when the device next syncs." />
        )}
        {events.isError && (
          <Text size="sm" c="danger" ta="center" py="lg" px="lg" {...(rows.length > 0 ? { style: { borderTop: HAIRLINE } } : {})}>{errorText(events.error)}</Text>
        )}
        {halves.isError && (
          <Text size="sm" c="danger" ta="center" py="sm" px="lg" style={{ borderTop: HAIRLINE }}>How these shares ended, and who was on the other end, could not be loaded: {errorText(halves.error)}</Text>
        )}
        <PageFooter page={page} total={total} noun="events" onChange={setPage} />
      </Paper>

      <PaneDrawer opened={paneOpen && selected !== null} onClose={() => setPaneOpen(false)}>
        {selected && (
          <EventDetail
            e={selected}
            extra={<>
              {selectedPeer && (
                <DrawerSection title={selectedPeer.direction === 'From' ? 'Sent from' : 'Sent to'}>
                  <EventTerms rows={[
                    selectedPeer.name && ['Operator', selectedPeer.name],
                    selectedPeer.org && ['Organization', selectedPeer.org],
                    selectedPeer.device && ['Device', <span key="d" className="id" style={WRAP}>{selectedPeer.device}</span>],
                  ]} />
                  <Text size="xs" c="dimmed">{selectedPeer.confirmed ? `${PEER_CONFIRMED}, as the console has it.` : `${PEER_CLAIMED}, not checked by the console. It is checked once the other device's own record arrives.`}</Text>
                </DrawerSection>
              )}
              {selectedEnd && (
              <DrawerSection title="How it ended">
                <EventTerms rows={[
                  ['When', when(selectedEnd.occurredAt)],
                  ['Reason', detailText(selectedEnd, 'reason')],
                  detailNumber(selectedEnd, 'minutes') !== null && ['Minutes', <span key="m" className="num">{detailNumber(selectedEnd, 'minutes')}</span>],
                  detailText(selectedEnd, 'ended') && ['Ended', detailText(selectedEnd, 'ended')],
                  typeof selectedEnd.detail?.['exposed'] === 'boolean' && ['Exposed', selectedEnd.detail['exposed'] ? 'yes' : 'no'],
                  ['Recorded', when(selectedEnd.receivedAt)],
                ]} />
              </DrawerSection>
              )}
            </>}
          />
        )}
      </PaneDrawer>
    </>
  );
}
